// Web Audio "jam engine" — synchronized, gapless session playback.
//
// The old play-all fired N independent <audio> elements, each streaming a lossy
// transcode and starting whenever it happened to buffer: the loops drifted apart
// and clicked at the seam (Opus/AAC carry encoder padding). This engine fixes
// both. One AudioContext decodes every loop's WAV into an AudioBuffer (WAV has no
// codec padding, so looping the whole buffer is gapless), and all sources are
// started at a single shared clock time so the loops begin together and stay
// phase-locked. A GainNode per track gives real per-loop volume/mute/solo.
//
// This is a singleton — one engine for the whole app — because a session is a
// single mix, and mixing HTMLAudio previews with Web Audio playback of the same
// loop would double it. Callers should pause single-loop previews before play-all
// and vice-versa.
//
// Loads use /download?...&play=1 (skips the download-count increment) so hitting
// play doesn't inflate a creator's download metric.

// A linear gain ramp from `from` to `to` over `fade` seconds starting at
// `when` (ctx time). Without the scheduling methods (old engines), jump.
function rampGain(param, from, to, when, fade) {
  if (typeof param.setValueAtTime === "function" && typeof param.linearRampToValueAtTime === "function") {
    if (typeof param.cancelScheduledValues === "function") param.cancelScheduledValues(0);
    param.setValueAtTime(from, when);
    param.linearRampToValueAtTime(to, when + fade);
  } else {
    param.value = to;
  }
}

// The first ctx time at or after `earliest` that is a whole number of
// `cycle`s from `zero`.
export function nextCycle(zero, earliest, cycle) {
  if (!(cycle > 0)) return earliest;
  const n = Math.ceil((earliest - zero) / cycle - 1e-9);
  let t = zero + Math.max(0, n) * cycle;
  if (t < earliest - 1e-9) t += cycle;
  return t;
}

class JamEngine {
  constructor() {
    this._ctx = null;
    // hash -> { buffer, gain, source, volume, muted }
    this._tracks = new Map();
    this._loading = new Map(); // hash -> Promise, dedupes concurrent loads
    this.playing = false;
    this._soloHash = null;
    this._startAt = 0; // ctx time all sources were scheduled to begin
    this._lastAudible = 0; // keeps _audibleTime() monotonic
    this._armed = false; // document-level iOS unlock installed
    // Bumped by every play(), stop() and reset(). A play() that finds it
    // moved after an await was cancelled meanwhile (a preview paused Play
    // all while the mix was still decoding) and starts nothing.
    this._gen = 0;
    this._epoch = 0; // bumped by reset(): a load landing after it is dropped
  }

  _ctxOrCreate() {
    if (!this._ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      this._ctx = new AC();
    }
    return this._ctx;
  }

  isLoaded(hash) {
    const t = this._tracks.get(hash);
    return !!(t && t.buffer);
  }

  // Fetch + decode one loop's WAV into a buffer with its own gain node.
  // Idempotent and de-duped across concurrent callers.
  loadTrack(hash, url) {
    if (this.isLoaded(hash)) return Promise.resolve();
    if (this._loading.has(hash)) return this._loading.get(hash);

    const ctx = this._ctxOrCreate();
    const epoch = this._epoch;
    const p = fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`load ${hash}: ${r.status}`);
        return r.arrayBuffer();
      })
      // Safari's decodeAudioData is callback-only, so wrap it in a Promise.
      .then(
        (arr) =>
          new Promise((resolve, reject) =>
            ctx.decodeAudioData(arr, resolve, reject),
          ),
      )
      .then((buffer) => {
        if (epoch !== this._epoch) return; // reset() meanwhile: a new session
        const gain = ctx.createGain();
        gain.connect(ctx.destination);
        const existing = this._tracks.get(hash) || { volume: 1, muted: false };
        gain.gain.value = existing.muted ? 0 : existing.volume;
        this._tracks.set(hash, {
          ...existing,
          buffer,
          gain,
          source: null,
        });
      })
      .finally(() => this._loading.delete(hash));

    this._loading.set(hash, p);
    return p;
  }

  // A one-frame silent buffer. Starting one inside a user gesture is what
  // actually unlocks audio on iOS (the classic touchstart/touchend trick).
  _playSilentFrame(ctx) {
    try {
      const buffer = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(ctx.destination);
      src.start(0);
    } catch (e) {
      /* best-effort */
    }
  }

  // On iOS a default AudioContext plays into the *ambient* audio session, which
  // the hardware ring/silent switch mutes. The context still reports 'running',
  // the sources still start and nothing throws — it is simply inaudible. This is
  // why <audio> loop previews were audible on the same page while play-all was
  // silent: HTMLMediaElement uses the media session, which the switch ignores.
  //
  // Claiming the playback session routes Web Audio the same way. Safari 16.4+;
  // a no-op everywhere else. Deliberately only called when the user actually
  // starts playback — claiming it on page load would interrupt whatever they're
  // already listening to just for opening a session.
  _claimPlaybackSession() {
    try {
      if (navigator.audioSession) navigator.audioSession.type = "playback";
    } catch (e) {
      /* unsupported — best effort */
    }
  }

  // iOS Safari starts an AudioContext suspended and only unlocks it if audio is
  // triggered *within* the tap. We start the real sources after an async fetch,
  // which iOS treats as outside the gesture — so unlock synchronously here by
  // playing a one-frame silent buffer, or playback stays dead silent (no error).
  // Must run in the same tick as the user gesture (before any await).
  _unlock(ctx) {
    this._claimPlaybackSession();
    this._playSilentFrame(ctx);
    return ctx.resume ? ctx.resume() : Promise.resolve();
  }

  // Belt and braces for the unlock: iOS only counts a gesture it considers
  // "activating", and a framework @click handler can arrive too late in the
  // touch sequence to qualify. Warming the context from a document-level
  // touchstart/touchend means it's already running before play-all is ever
  // tapped. Listeners drop themselves once the context is running.
  // Idempotent; safe to call on every mount.
  arm() {
    if (this._armed || typeof document === "undefined") return;
    this._armed = true;
    const events = ["touchstart", "touchend", "mousedown", "keydown"];
    const remove = () =>
      events.forEach((e) => document.removeEventListener(e, warm, true));
    const warm = () => {
      const ctx = this._ctxOrCreate();
      this._playSilentFrame(ctx);
      const done = ctx.resume ? ctx.resume() : Promise.resolve();
      Promise.resolve(done)
        .then(() => {
          if (ctx.state === "running") remove();
        })
        .catch(() => {
          /* keep listening for the next gesture */
        });
    };
    events.forEach((e) =>
      document.addEventListener(e, warm, { capture: true, passive: true }),
    );
  }

  // Play a whole session in sync. `loops` is [{ hash, url }]. `offset` is the
  // position (seconds) to start from — each loop starts at offset modulo its
  // own length, so the mix stays phase-locked. Must be invoked from a user
  // gesture (browser autoplay policy) — the unlock is kicked off synchronously
  // before any await for iOS.
  // Resolves false, starting nothing, when stop(), reset() or another
  // play() came first.
  async play(loops, offset = 0) {
    const gen = ++this._gen;
    const ctx = this._ctxOrCreate();
    const resumed = this._unlock(ctx);
    await Promise.all(loops.map((l) => this.loadTrack(l.hash, l.url)));
    if (gen !== this._gen) return false;
    await resumed;
    if (gen !== this._gen) return false;

    // One start time for every track → they begin together and loop in phase.
    const startAt = ctx.currentTime + 0.12;
    const started = this._startSources(
      loops.map((l) => l.hash),
      startAt,
      offset,
    );
    this.playing = started > 0;
    return this.playing;
  }

  // A loop landed (or went) while the mix plays: decode the new mix, then
  // switch to it on the shared clock, crossfading over `fade` seconds. The
  // app launches a new layer on the next cycle, its frame 0 on a loop top
  // (D7); here every mix tiles its loops from zero, so the switch waits for
  // the next multiple of `alignTo()` seconds from zero (the new layer's
  // length; the new mix's when unknown), where the new layer is at its frame
  // 0 and the layers already sounding are exactly where they were. alignTo()
  // below zero (a loop went, nothing new) switches now. Resolves false,
  // changing nothing, when playback stopped or a newer swap was asked for
  // meanwhile.
  async swapMix(toHash, url, { fade = 0.03, lead = 0.05, alignTo = null } = {}) {
    if (!this.playing || !this._ctx) return false;
    const gen = this._gen;
    const seq = (this._swapSeq = (this._swapSeq || 0) + 1);
    await this.loadTrack(toHash, url);
    if (gen !== this._gen || seq !== this._swapSeq || !this.playing) return false;
    const to = this._tracks.get(toHash);
    if (!to || !to.buffer || !(to.buffer.duration > 0)) return false;
    const ctx = this._ctx;
    const d = to.buffer.duration;
    const asked = typeof alignTo === "function" ? Number(alignTo()) : 0;
    const now = ctx.currentTime + lead;
    const when = asked < 0 ? now : nextCycle(this._startAt, now, asked > 0 ? asked : d);
    const offset = (((when - this._startAt) % d) + d) % d;
    const level = to.muted ? 0 : to.volume;
    this._stopSource(to);
    const source = ctx.createBufferSource();
    source.buffer = to.buffer;
    source.loop = true;
    source.connect(to.gain);
    rampGain(to.gain.gain, 0, level, when, fade);
    source.start(when, offset);
    to.source = source;
    // Everything else sounding fades out and goes.
    for (const [hash, t] of this._tracks) {
      if (hash === toHash || !t.source) continue;
      rampGain(t.gain.gain, t.gain.gain.value, 0, when, fade);
      const old = t.source;
      t.source = null;
      try {
        old.stop(when + fade + 0.01);
      } catch (e) {
        /* already stopped */
      }
      this._dropLater(hash, old, (lead + fade + 0.25) * 1000);
    }
    return true;
  }

  // Forget a swapped-out mix once its source has stopped (unless it came
  // back as the current one meanwhile).
  _dropLater(hash, source, ms) {
    setTimeout(() => {
      const t = this._tracks.get(hash);
      if (!t || t.source) return;
      try {
        source.disconnect();
      } catch (e) {
        /* noop */
      }
      try {
        t.gain && t.gain.disconnect();
      } catch (e) {
        /* noop */
      }
      this._tracks.delete(hash);
    }, ms);
  }

  // Jump every playing track to `offset` seconds, keeping them phase-locked.
  // Buffer sources can't be repositioned, so each is replaced by a fresh one
  // started at the new offset. Returns false when nothing is playing.
  seek(offset) {
    if (!this.playing || !this._ctx) return false;
    const hashes = [...this._tracks.keys()].filter(
      (h) => this._tracks.get(h).source,
    );
    return this._startSources(hashes, this._ctx.currentTime + 0.02, offset) > 0;
  }

  // (Re)start the given tracks at ctx time `startAt`, `offset` seconds in.
  _startSources(hashes, startAt, offset) {
    const ctx = this._ctx;
    const pos = Math.max(0, Number(offset) || 0);
    // loopPhase() measures from here, so a mid-loop start reads correctly.
    this._startAt = startAt - pos;
    let started = 0;
    for (const hash of hashes) {
      const t = this._tracks.get(hash);
      if (!t || !t.buffer) continue;
      // Replace any prior source so play()/seek() are safely re-entrant.
      this._stopSource(t);
      const source = ctx.createBufferSource();
      source.buffer = t.buffer;
      source.loop = true; // whole-buffer loop; WAV → gapless
      source.connect(t.gain);
      this._applyGain(hash, t);
      source.start(startAt, pos % t.buffer.duration);
      t.source = source;
      started += 1;
    }
    return started;
  }

  stop() {
    this._gen += 1; // cancels a play() still loading
    for (const t of this._tracks.values()) this._stopSource(t);
    this.playing = false;
  }

  // Whether the context is actually running (producing sound). iOS can leave it
  // 'suspended'/'interrupted' even after resume(), so callers use this to detect
  // a silent start and fall back to an <audio> element.
  isRunning() {
    return !!(this._ctx && this._ctx.state === "running");
  }

  // Phase (0-1) of a loop within its current cycle, from the shared clock.
  // The layered mix has no single timeline, so the bar shows the phase of one
  // representative loop as a moving cursor. null when not playing / unknown.
  loopPhase(hash) {
    if (!this.playing || !this._ctx) return null;
    const t = this._tracks.get(hash);
    if (!t || !t.buffer || !(t.buffer.duration > 0)) return null;
    const elapsed = this._audibleTime() - this._startAt;
    if (elapsed < 0) return 0;
    return (elapsed % t.buffer.duration) / t.buffer.duration;
  }

  // Context time of the sample reaching the speakers right now. currentTime is
  // the render clock: it leads the output by the output latency and, in
  // Firefox, advances in coarse callback-sized steps, so a cursor driven by it
  // stutters and runs ahead of the audio. getOutputTimestamp() pins a context
  // time to the moment it was output; extrapolating from it with
  // performance.now() is smooth and in step with what's heard. Clamped so it
  // never runs backwards or past the render clock; falls back to currentTime.
  _audibleTime() {
    const ctx = this._ctx;
    let t = ctx.currentTime;
    if (typeof ctx.getOutputTimestamp === "function") {
      const ts = ctx.getOutputTimestamp();
      if (ts && ts.contextTime > 0 && ts.performanceTime > 0) {
        const est =
          ts.contextTime + (performance.now() - ts.performanceTime) / 1000;
        t = Math.min(Math.max(est, this._lastAudible), ctx.currentTime);
      }
    }
    this._lastAudible = t;
    return t;
  }

  // Length (seconds) of a loaded loop's buffer, or 0 when not loaded.
  duration(hash) {
    const t = this._tracks.get(hash);
    return t && t.buffer ? t.buffer.duration : 0;
  }

  // Position (seconds) within a loop's current cycle; null when not playing.
  position(hash) {
    const ph = this.loopPhase(hash);
    return ph == null ? null : ph * this.duration(hash);
  }

  _stopSource(track) {
    if (track && track.source) {
      try {
        track.source.stop();
      } catch (e) {
        /* already stopped */
      }
      try {
        track.source.disconnect();
      } catch (e) {
        /* noop */
      }
      track.source = null;
    }
  }

  // --- per-track mixing -----------------------------------------------------

  setVolume(hash, value) {
    const v = Math.max(0, Math.min(1, Number(value)));
    const t = this._tracks.get(hash) || { volume: 1, muted: false };
    t.volume = v;
    this._tracks.set(hash, t);
    this._applyGain(hash, t);
  }

  setMuted(hash, muted) {
    const t = this._tracks.get(hash) || { volume: 1, muted: false };
    t.muted = !!muted;
    this._tracks.set(hash, t);
    this._applyGain(hash, t);
  }

  // Solo: null clears; a hash mutes every other track for the mix.
  setSolo(hash) {
    this._soloHash = hash || null;
    for (const h of this._tracks.keys()) this._applyGain(h, this._tracks.get(h));
  }

  isMuted(hash) {
    const t = this._tracks.get(hash);
    return !!(t && t.muted);
  }

  volumeOf(hash) {
    const t = this._tracks.get(hash);
    return t ? t.volume : 1;
  }

  _applyGain(hash, track) {
    if (!track || !track.gain || !this._ctx) return;
    const soloed = this._soloHash && this._soloHash !== hash;
    const target = track.muted || soloed ? 0 : track.volume;
    // Short ramp avoids a click when toggling.
    const now = this._ctx.currentTime;
    track.gain.gain.setTargetAtTime(target, now, 0.01);
  }

  // Drop everything (e.g. leaving the session) so a new session starts clean.
  reset() {
    this._epoch += 1;
    this.stop();
    for (const t of this._tracks.values()) {
      try {
        t.gain && t.gain.disconnect();
      } catch (e) {
        /* noop */
      }
    }
    this._tracks.clear();
    this._loading.clear();
    this._soloHash = null;
  }
}

export default new JamEngine();
